<template>
  <b-container class="mt-2" style="width:400px">
    <b-card v-if="membership" header="TWA Enrollment" class="text-center">
      <b-card-text class="text-left">
        <!-- Course info - shown across all views -->
        <div class="mb-4 pb-3 border-bottom">
          <div><strong>Course:</strong> {{ membership.context.context_title }}</div>
          <div v-if="membership.context.product">
            <strong>Unit(s):</strong> {{ membership.context.product.name }}
          </div>
        </div>

        <div v-if="show_form === 'agree'">
          <template v-if="hasEnrollAction">
            <b>Review Terms</b>
            <div class="mt-2 mb-4">
              <b-form-checkbox v-model="form.agreed" name="agreed" value="yes" unchecked-value="no">
                Yes, I have read and agree with TWA Textbook's
                <a href="https://truewayasl.com/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
                and <a href="https://truewayasl.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </b-form-checkbox>
            </div>
          </template>
          <p v-if="auto_enroll_available">
            Review the terms and click Enroll to complete your enrollment.
          </p>
          <p v-else-if="isBlocked" class="text-danger">
            {{ blockMessage }}
          </p>
          <p v-else-if="card_available && accepts_access_code">
            Please choose below to enter your access code or to make payment to complete your enrollment.
          </p>
          <p v-else-if="card_available">
            Please click below to make payment to complete your enrollment.
          </p>
          <p v-else>
            Please click below to enter your access code to complete your enrollment.
          </p>
          <div v-if="auto_enroll_available" class="mt-4">
            <b-button variant="primary" block :disabled="form.agreed === 'no'" @click="autoEnroll">Agree &amp; Enroll
              <b-spinner v-if="isSending" class="m-1" label="Spinning" small/>
            </b-button>
          </div>
          <div v-else-if="isBlocked && !cardUnavailable" class="mt-4">
            <b-button variant="outline-primary" block @click="openBeacon">Contact TWA</b-button>
          </div>
          <div v-else-if="!isBlocked" class="mt-4 text-center">
            <b-button v-if="accepts_access_code" class="mr-3" variant="primary" :disabled="form.agreed === 'no'"
                      @click="show_form = 'coupons'">Enter Access Code
            </b-button>
            <b-button v-if="card_available" variant="primary"
                      :disabled="form.agreed === 'no'" @click="checkout">Make Payment
            </b-button>
          </div>
          <!-- TWA-201: shown whether or not the access-code path is still open -->
          <template v-if="cardUnavailable">
            <p v-if="!isBlocked" class="mt-4 mb-2 text-danger">{{ blockMessage }}</p>
            <div class="text-center" :class="{'mt-4': isBlocked}">
              <b-button class="mr-2" :variant="isBlocked ? 'primary' : 'outline-primary'" :disabled="isRetrying" @click="retryLoad">Try Again
                <b-spinner v-if="isRetrying" class="m-1" label="Retrying" small/>
              </b-button>
              <b-button variant="outline-primary" @click="openBeacon">Contact TWA</b-button>
            </div>
          </template>
        </div>

        <div v-else-if="show_form === 'coupons'">
          <b>Enter Access Code</b>
          <p class="mt-2">Enter your access code to complete your enrollment.</p>
          <!-- TWA-589: the second sentence names a button, so only when that button exists -->
          <p>
            Access codes are usually sold by the bookstore or given by your school.
            <template v-if="card_available">If you don't have one, you can pay by card by choosing the "Make Payment" button.</template>
          </p>
          <b-input-group prepend="Access Code" class="mt-2">
            <b-form-input maxlength="20" v-model.trim="form.coupon" type="text"></b-form-input>
          </b-input-group>
          <ul class="mt-3 list-unstyled">
            <li v-for="error in coupon_errors" :key="error" class="text-danger">{{ error }}</li>
          </ul>
          <b-button v-if="card_available" class="mr-2" variant="outline-primary" :disabled="isSending" @click="backToAgreement">Back</b-button>
          <b-button class="mr-2" variant="primary" @click="verifyCoupon">Submit
            <b-spinner v-if="isSending" class="m-1" label="Spinning" small/>
          </b-button>
          <b-button v-if="requiresSupport" variant="outline-primary" @click="openBeacon">
            Contact TWA
          </b-button>
        </div>

        <div v-else-if="show_form === 'thank_you'">
          <p>Congratulations! You're enrolled in a TWA Textbook course.</p>
          <p>You may close this window and return to your course.</p>
          <p class="mt-3">Enjoy learning ASL!</p>
        </div>

        <div v-else>
          Hang on. Getting info...
          <b-spinner class="ml-2" style="width: 1.5rem; height: 1.5rem;"
                     variant="secondary"></b-spinner>
        </div>

        <stripe-checkout ref="checkoutRef" :pk="stripe_key" :session-id="session_id"/>
      </b-card-text>
    </b-card>
    <div v-if="membership" class="text-muted small text-center mt-2">
      token: {{ membership.token }}
    </div>
    <b-card v-else-if="errorMessage || isRetrying" header="TWA Enrollment" class="text-center">
      <b-card-text>
        <h5>Hmm...</h5>
        <p class="text-danger">{{ errorMessage }}</p>
        <b-button class="mr-2" variant="primary" @click="getMembership" :disabled="isRetrying">Try Again
          <b-spinner v-if="isRetrying" class="m-1" label="Retrying" small/>
        </b-button>
        <b-button v-if="requiresSupport" variant="outline-primary" @click="openBeacon">
          Contact TWA
        </b-button>
      </b-card-text>
    </b-card>
    <b-card v-else header="TWA Enrollment" class="text-center">
      Hang on. Getting info...
      <b-spinner class="ml-2" style="width: 1.5rem; height: 1.5rem;"
                 variant="secondary"></b-spinner>
    </b-card>
  </b-container>
</template>

<script>
import {verifyAccessCode, getMembership, autoEnrollMembership} from "../../utils/lti-api"
import {StripeCheckout} from '@vue-stripe/vue-stripe'
import {getErrorMessage} from "../../utils/error-messages"
import {initBeacon, setBeaconData, clearBeaconError, openBeacon, identifyBeaconUser, reportErrorToBeacon, reportValidationErrorToBeacon} from "../../utils/beacon"
import {MIN_COUPON_LENGTH} from "../../utils/constants"

export default {
  components: {
    StripeCheckout,
  },
  data() {
    return {
      form: {
        agreed: 'no',
        coupon: null
      },
      isSending: false,
      isRetrying: false,
      show_form: 'default',
      auto_enroll_available: false,
      auto_enroll_state: null,
      auto_enroll_level: null,
      coupon_errors: [],
      membership: null,
      errorMessage: null,
      beaconContext: {},
    }
  },
  computed: {
    token() {
      return this.$route.query.token || null
    },
    accepts_payment() {
      // NULL must count as OFF to match the backend: Membership#session_id returns nil
      // unless the credential's accepts_payment is truthy, so defaulting to true here
      // would render a Make Payment button bound to a null session_id (TWA-323).
      return this.membership?.context?.deployment?.credential?.accepts_payment ?? false
    },
    accepts_access_code() {
      // NULL must hide the form: the server gate (TWA-322) refuses a NULL flag, so
      // `?? true` would offer a form whose submit is always rejected — the same
      // backend/frontend NULL inversion TWA-323 fixed for accepts_payment.
      return this.membership?.context?.deployment?.credential?.accepts_access_code ?? false
    },
    // TWA-201: the card button must follow the SESSION, not just the flag. A null
    // session_id is the API's "no checkout available" answer, so accepts_payment can be
    // true with no session to send the student to — rendering the button there is a dead
    // click. Deliberately does not care WHY it is null (price archived, Stripe down, card
    // payment off): the button is only ever as good as the session behind it. The
    // access-code path is untouched; when there is no code path either, hasEnrollAction
    // falls through to the existing Contact TWA dead end.
    card_available() {
      return this.accepts_payment && this.session_id !== null
    },
    // TWA-201: the school takes cards but Stripe handed back no session.
    // ⚠️ The !autoEnrollMode guard is belt-and-braces, NOT load-bearing: whenever a binding
    // is live both controllers already force accepts_payment false in the payload (TWA-242 —
    // api/lti_controller.rb, lti/data_controller.rb), so a bound course should never arrive
    // here with the flag true. Kept because if a stale or hand-built payload ever did, that
    // student needs the closed-course message rather than a retry button that cannot help.
    // Scoped to the agreement screen: a processed membership also has a null session_id, and
    // without this every returning student would report 'card_unavailable' to Beacon.
    cardUnavailable() {
      return this.show_form === 'agree' && !this.autoEnrollMode && this.accepts_payment && !this.card_available
    },
    autoEnrollMode() {
      // A bound auto-enroll code puts the credential in site-license mode: enroll-or-block, no self-pay.
      // auto_enroll_state is a bound state (available/at_cap/inactive/expired/mismatch/multi_product;
      // inactive = a turned-off code, TWA-760, which takes the generic block message below)
      // vs 'none'/null when unbound.
      return this.auto_enroll_state && this.auto_enroll_state !== 'none'
    },
    blockMessage() {
      if (this.auto_enroll_state === 'at_cap') {
        // auto_enroll_level says WHICH pool ran dry (TWA-230). Default to the school
        // wording when absent (older API response) — that was the only wording before.
        return this.auto_enroll_level === 'course'
          ? 'Your course has run out of available spots. Please let your teacher know.'
          : 'Your school has run out of available spots. Please let your teacher know.'
      }
      // TWA-201: no card session is a Stripe problem, not a closed course — retry, don't
      // send the student to their teacher.
      if (this.cardUnavailable) {
        return 'Card payment is temporarily unavailable. Please try again or contact TWA.'
      }
      return "This course isn't available to enroll right now. Please let your teacher know."
    },
    hasEnrollAction() {
      // The agree checkbox only makes sense when there's an actionable button — Agree & Enroll,
      // or the self-pay pay/code buttons. When the student is blocked, there's nothing to agree to.
      return this.auto_enroll_available || (!this.autoEnrollMode && (this.card_available || this.accepts_access_code))
    },
    isBlocked() {
      // On the agreement screen with no available action — a bound code at cap / expired /
      // mismatch, or a consumer with no code and no self-pay. A dead end; we offer support.
      return this.show_form === 'agree' && !this.hasEnrollAction
    },
    stripe_key() {
      return process.env.VUE_APP_STRIPE_KEY
    },
    session_id() {
      return this.membership?.session_id ?? null
    },
    requiresSupport() {
      // Always show "Contact TWA" button on any error
      return this.errorMessage || this.coupon_errors.length > 0
    }
  },
  methods: {
    // TWA-589: the agreement checkbox stays ticked; only the code errors are dropped
    backToAgreement() {
      this.coupon_errors = []
      this.show_form = 'agree'
    },
    async getMembership() {
      if (this.errorMessage) {
        this.isRetrying = true
        this.errorMessage = null
      }
      if (this.token) {
        try {
          // Small delay for smoother UX transition
          await new Promise(resolve => setTimeout(resolve, 500))

          const response = await getMembership(this.token)
          this.membership = response.membership
          this.show_form = response.action || 'agree'

          this.auto_enroll_available = response.auto_enroll_available || false
          this.auto_enroll_state = response.auto_enroll_state || null
          this.auto_enroll_level = response.auto_enroll_level || null

          // Rebuild the full course context here (not just in mounted) so a
          // Try Again retry doesn't leave the pre-load stub as the context
          // every later report re-asserts
          this.buildSessionDataForBeacon()

          // Clear Beacon error data on successful load
          clearBeaconError(this.beaconContext)

          // Blocked, or card-less with only the code path left — attach the reason so support can diagnose.
          if (this.isBlocked || this.cardUnavailable) {
            setBeaconData({
              ...this.beaconContext,
              // TWA-201: auto_enroll_state is the string 'none' when unbound, which is truthy —
              // so the old `|| 'no_self_pay'` fallback reported 'none' and the new reason would
              // never surface. Branch on autoEnrollMode, which already treats 'none' as unbound.
              'Enrollment Blocked': this.autoEnrollMode
                ? this.auto_enroll_state
                // card_unavailable_code_open: card is down but the student can still use a code — not blocked.
                : (this.cardUnavailable
                  ? (this.isBlocked ? 'card_unavailable' : 'card_unavailable_code_open')
                  : 'no_self_pay'),
              'User Message': this.blockMessage
            })
          }
        } catch (error) {
          this.errorMessage = getErrorMessage(error)
          reportErrorToBeacon(error, 'Page Load Error', this.beaconContext)
        }
      } else {
        this.errorMessage = 'No membership token provided'
        reportValidationErrorToBeacon(this.errorMessage, 'Missing Token', this.beaconContext)
      }
      this.isRetrying = false
    },
    // TWA-201: retry from the blocked screen. getMembership only raises isRetrying when it is
    // recovering from a failed page load; here the page already rendered, so drive it here. A
    // failed retry sets an errorMessage nothing on this screen renders — toast it and clear it.
    async retryLoad() {
      this.isRetrying = true
      await this.getMembership()
      if (this.errorMessage) {
        this.notifyMe(this.errorMessage)
        this.errorMessage = null
      }
    },
    async verifyCoupon() {
      this.isSending = true
      // Freeze the code before the await — the input stays editable while the
      // request is in flight, so form.coupon may be mid-retype by the time an
      // error report reads it.
      const code = this.form.coupon
      try {
        if (this.checkCouponForm()) {
          let response = await verifyAccessCode(this.token, code)

          // Any 2xx means the attempt succeeded (the backend also returns 200
          // with no action for an already-processed membership) — clear
          // unconditionally, matching Pay.vue.
          clearBeaconError(this.beaconContext)

          // Backend returns action: 'enrolled' on success
          if (response.action === 'enrolled') {
            this.show_form = 'thank_you'
          }
          // Note: errors are now thrown as HTTP errors and handled in catch block
        } else {
          reportValidationErrorToBeacon(this.coupon_errors.join(' '), 'Access Code Error', this.beaconContext, code || '')
        }
      } catch (error) {
        this.coupon_errors.push(getErrorMessage(error))
        reportErrorToBeacon(error, 'Access Code Error', this.beaconContext, code)
      }
      this.isSending = false
    },
    async autoEnroll() {
      this.isSending = true
      try {
        const response = await autoEnrollMembership(this.token)
        clearBeaconError(this.beaconContext)
        if (response.action === 'enrolled') {
          this.show_form = 'thank_you'
        } else if (response.action === 'agree') {
          // Seat taken between load and click — reflect the new state (block).
          this.auto_enroll_available = response.auto_enroll_available || false
          this.auto_enroll_state = response.auto_enroll_state || 'at_cap'
          this.auto_enroll_level = response.auto_enroll_level || this.auto_enroll_level
        }
      } catch (error) {
        this.notifyMe(getErrorMessage(error))
        reportErrorToBeacon(error, 'Auto-Enroll Error', this.beaconContext)
      }
      this.isSending = false
    },
    async checkout() {
      try {
        this.$refs.checkoutRef.redirectToCheckout();
      } catch (error) {
        this.notifyMe(getErrorMessage(error))
        reportErrorToBeacon(error, 'Stripe Checkout Error', this.beaconContext)
      }
    },
    checkCouponForm() {
      this.coupon_errors = [];

      if (!this.form.coupon) {
        this.coupon_errors.push("Please enter the access code.");
      }

      if (this.form.coupon && this.form.coupon.length < MIN_COUPON_LENGTH) {
        this.coupon_errors.push("This access code is not recognized. Please check for typos.");
      }

      return !this.coupon_errors.length;
    },
    notifyMe(message, variant = 'danger') {
      this.$bvToast.toast(message, {
        title: 'Notification',
        solid: true,
        variant: variant,
        autoHideDelay: 2500,
        appendToast: true
      })
    },
    buildSessionDataForBeacon() {
      // Guard: only build session data if membership loaded successfully
      if (!this.membership) return

      // Store context for use in error calls (merge-vs-replace semantics undocumented; every call passes the full set)
      this.beaconContext = {
        'Flow': 'LTI 1.3',
        'Course': this.membership.context.context_title,
        'Domain': this.membership.context.deployment.credential.platform.iss,
        'Token': this.token,
        'Email': this.membership.email
      }

      setBeaconData(this.beaconContext)
      identifyBeaconUser(this.membership.email)
    },
    openBeacon
  },
  async mounted() {
    initBeacon()
    // Minimal context before the fetch: a page-load failure is exactly when support
    // most needs to know which membership was attempted. buildSessionDataForBeacon
    // replaces this with full course context on success.
    if (this.token) {
      this.beaconContext = { 'Flow': 'LTI 1.3', 'Token': this.token }
      setBeaconData(this.beaconContext)
    }
    await this.getMembership()
  }
};
</script>

